// Canned Responses Manager & Picker Modal (WP-04).
import { useState, useEffect, useCallback, useId, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { useTenant } from '../../lib/tenant';
import { cn } from '../../lib/cn';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  listCannedResponses,
  saveCannedResponse,
  deleteCannedResponse,
  resolveCannedPlaceholders,
  type CannedResponseDoc,
} from '../../lib/canned';
import { uploadCannedImageFile } from '../../lib/media';
import type { LiveConversation } from '../../lib/inboxTypes';
import {
  CloseIcon,
  PlusIcon,
  TrashIcon,
  EditIcon,
  CheckIcon,
  UploadIcon,
  FileTextIcon,
} from '../icons';

interface CannedResponsesModalProps {
  onClose: () => void;
  onSelect?: (text: string, imagePaths?: string[]) => void;
  activeConv?: LiveConversation | null;
}

export default function CannedResponsesModal({
  onClose,
  onSelect,
  activeConv,
}: CannedResponsesModalProps) {
  const { t } = useI18n();
  const { tenantId } = useTenant();
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<CannedResponseDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingItem, setEditingItem] = useState<Partial<CannedResponseDoc> | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listCannedResponses({});
      if (res.ok && Array.isArray(res.items)) {
        setItems(res.items);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to fetch canned responses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleStartCreate = () => {
    setEditingItem({
      name: '',
      text: '',
      imagePaths: [],
    });
  };

  const handleStartEdit = (item: CannedResponseDoc) => {
    setEditingItem({ ...item });
  };

  const handleInsertPlaceholder = (placeholder: string) => {
    if (!editingItem) return;
    setEditingItem({
      ...editingItem,
      text: (editingItem.text || '') + `{{${placeholder}}}`,
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenantId) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('canned.imageTooLarge'));
      return;
    }

    setUploadingImage(true);
    try {
      const { downloadUrl } = await uploadCannedImageFile(tenantId, file, file.name);
      const currentImages = editingItem?.imagePaths || [];
      if (currentImages.length >= 5) {
        toast.error(t('canned.maxImages'));
        return;
      }
      setEditingItem({
        ...editingItem,
        imagePaths: [...currentImages, downloadUrl],
      });
    } catch (err) {
      toast.error((err as Error).message || t('canned.uploadFailed'));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    if (!editingItem) return;
    const next = [...(editingItem.imagePaths || [])];
    next.splice(index, 1);
    setEditingItem({ ...editingItem, imagePaths: next });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem?.name?.trim() || !editingItem?.text?.trim()) return;

    setSaving(true);
    try {
      await saveCannedResponse({
        id: editingItem.id,
        name: editingItem.name.trim(),
        text: editingItem.text.trim(),
        imagePaths: editingItem.imagePaths || [],
      });
      setEditingItem(null);
      await fetchItems();
    } catch (err) {
      toast.error((err as Error).message || t('canned.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: t('canned.deleteConfirm'), body: t('common.cannotUndo'), danger: true }))) return;
    try {
      await deleteCannedResponse({ id });
      setItems((prev) => prev.filter((it) => it.id !== id));
      if (editingItem?.id === id) setEditingItem(null);
    } catch (err) {
      toast.error((err as Error).message || t('canned.deleteFailed'));
    }
  };

  const handlePickResponse = (item: CannedResponseDoc) => {
    if (!onSelect) return;
    const resolved = resolveCannedPlaceholders(item.text, activeConv);
    onSelect(resolved, item.imagePaths);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-[85vh] max-h-[640px] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
              <FileTextIcon size={18} />
            </div>
            <div>
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {t('canned.modalTitle') || 'Canned Responses'}
              </h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {onSelect
                  ? t('canned.pickSubtitle') || 'Click a response to insert into message'
                  : t('canned.manageSubtitle') || 'Create and manage reusable message templates'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editingItem && (
              <button
                type="button"
                onClick={handleStartCreate}
                className="flex items-center gap-1.5 rounded-xl bg-brand px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-brand-dark"
              >
                <PlusIcon size={14} />
                <span>{t('canned.create') || 'New Response'}</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close') || 'Close'}
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {editingItem ? (
            /* Edit / Create Form */
            <form onSubmit={handleSave} className="space-y-4">
              <div className="flex items-center justify-between border-b border-line pb-3 dark:border-slate-800">
                <span className="text-xs font-bold uppercase tracking-wider text-ink dark:text-slate-200">
                  {editingItem.id ? t('canned.editTitle') || 'Edit Canned Response' : t('canned.newTitle') || 'New Canned Response'}
                </span>
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  {t('common.backToList') || 'Back to list'}
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-ink mb-1 dark:text-slate-200">
                  {t('canned.name') || 'Response Name / Shortcut'} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editingItem.name || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                  placeholder={t('canned.namePlaceholder') || 'e.g., Delivery Hours, Bank Details…'}
                  className="w-full rounded-xl border border-line bg-surface px-3.5 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-ink dark:text-slate-200">
                    {t('canned.text') || 'Message Content'} <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex items-center gap-1 text-[11px]">
                    <span className="text-ink-4 dark:text-slate-500">{t('canned.insert') || 'Insert:'}</span>
                    {['name', 'phone', 'date', 'time'].map((ph) => (
                      <button
                        key={ph}
                        type="button"
                        onClick={() => handleInsertPlaceholder(ph)}
                        className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                      >
                        +{ph}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  rows={4}
                  required
                  value={editingItem.text || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                  placeholder={t('canned.textPlaceholder') || 'Hello {{name}}, our delivery hours are…'}
                  className="w-full rounded-xl border border-line bg-surface p-3 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              {/* Attach Images (max 5) */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-ink dark:text-slate-200">
                    {t('canned.images') || 'Images (max 5, ≤2MB each)'}
                  </label>
                  {(editingItem.imagePaths?.length || 0) < 5 && (
                    <button
                      type="button"
                      disabled={uploadingImage}
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1 text-xs font-bold text-primary hover:underline disabled:opacity-50"
                    >
                      <UploadIcon size={14} />
                      <span>{uploadingImage ? t('common.uploading') || 'Uploading…' : t('canned.addImage') || 'Add Image'}</span>
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>

                {editingItem.imagePaths && editingItem.imagePaths.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {editingItem.imagePaths.map((imgUrl, idx) => (
                      <div key={imgUrl} className="relative h-16 w-16 overflow-hidden rounded-lg border border-line bg-slate-100 dark:border-slate-800 dark:bg-slate-800">
                        <img src={imgUrl} alt="" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          aria-label={t('common.delete') || 'Delete'}
                          className="absolute end-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-rose-600 text-[10px] text-white shadow"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="rounded-xl border border-line px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={saving || !editingItem.name?.trim() || !editingItem.text?.trim()}
                  className="flex items-center gap-1.5 rounded-xl bg-brand px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-brand-dark disabled:opacity-50"
                >
                  <CheckIcon size={15} />
                  <span>{saving ? t('common.saving') || 'Saving…' : t('common.save') || 'Save Response'}</span>
                </button>
              </div>
            </form>
          ) : (
            /* Items List */
            <div>
              {error && (
                <div className="mb-3 rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="grid h-48 place-items-center text-xs text-ink-3 dark:text-slate-500">
                  {t('common.loading') || 'Loading responses…'}
                </div>
              ) : items.length === 0 ? (
                <div className="grid h-48 place-items-center text-center text-xs text-ink-3 dark:text-slate-500">
                  <div>
                    <p>{t('canned.empty') || 'No canned responses yet'}</p>
                    <button
                      type="button"
                      onClick={handleStartCreate}
                      className="mt-2 text-xs font-bold text-brand hover:underline"
                    >
                      {t('canned.createFirst') || 'Create your first canned response'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="group flex items-start justify-between gap-3 rounded-xl border border-line bg-surface p-3.5 transition-colors hover:border-slate-300 dark:border-slate-800 dark:bg-slate-850 dark:hover:border-slate-700"
                    >
                      <div
                        onClick={() => onSelect && handlePickResponse(item)}
                        className={cn('min-w-0 flex-1', onSelect && 'cursor-pointer')}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-ink dark:text-slate-100">
                            {item.name}
                          </span>
                          {item.imagePaths?.length > 0 && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                              📷 {item.imagePaths.length}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs text-ink-2 dark:text-slate-300">
                          {item.text}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {onSelect ? (
                          <button
                            type="button"
                            onClick={() => handlePickResponse(item)}
                            className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-brand-dark"
                          >
                            {t('canned.insert') || 'Insert'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => handleStartEdit(item)}
                          aria-label={t('common.edit') || 'Edit'}
                          className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          <EditIcon size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(item.id)}
                          aria-label={t('common.delete') || 'Delete'}
                          className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                        >
                          <TrashIcon size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
